import {
  Inject,
  Injectable,
  InjectionToken,
  NgZone,
  Optional
} from '@angular/core';
import { fetchScript } from '@ppl/utils/script.utils';
import { BehaviorSubject, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export const FIREBASE_CONFIG = new InjectionToken<string>('FirebaseConfig');

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {

  firebaseInitialized$ = new BehaviorSubject<boolean>(false);

  private retryAttempts = 0;
  private showInConsole = false;

  constructor(
    private ngZone: NgZone,
    @Optional() @Inject(FIREBASE_CONFIG) private firebaseConfig?: FirebaseConfig,
  ) {
    this.showInConsole = this.getConsoleLogValue();
  }

  init() {
    if (this.firebaseConfig && (this.firebaseConfig.enabled || this.showInConsole)) {
      from(this.injectFirebaseApp()).pipe(
        switchMap(() => this.injectFirebaseAnalytics())
      ).subscribe({
        next: () => {
          this.initFirebase();
        }
      });

    }
  }

  logEvent(eventAction: FirebaseEventsEnum, eventParams?: Partial<FirebaseLogData>) {
    if (this.firebaseConfig && (this.firebaseConfig.enabled || this.showInConsole)) {
      if (this.showInConsole) {
        console.log(eventAction, eventParams);
      }

      try {
        const analytics = (window as any).firebase.analytics();
        this.ngZone.runOutsideAngular(() => {
          analytics.logEvent(eventAction, eventParams);
        });
      } catch (error) { }
    }
  }

  setUserProperties(properties: { [key: string]: any; }) {
    if (this.showInConsole) {
      console.log(properties);
    }

    try {
      const analytics = getAnalytics();
      this.ngZone.runOutsideAngular(() => {
        analytics.setUserProperties(properties);
      });
    } catch (error) { }
  }

  private injectFirebaseApp() {
    return fetchScript({ url: `${baseUrl}firebase-app.js` });
  }

  private injectFirebaseAnalytics() {
    return fetchScript({ url: `${baseUrl}firebase-analytics.js` });
  }

  private initFirebase() {
    if (this.firebaseConfig && (this.firebaseConfig.enabled || this.showInConsole)) {
      try {
        const firebase = (window as any).firebase;
        this.ngZone.runOutsideAngular(() => {
          firebase.initializeApp(this.firebaseConfig.configData);
          this.firebaseInitialized$.next(true);
        });
        this.retryAttempts = maxRetryAttempts;
      } catch (error) {
        this.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            this.retryAttempts++;

            if (this.retryAttempts < maxRetryAttempts) {
              this.initFirebase();
            }
          }, 1000);
        });
      }
    }
  }

  private getConsoleLogValue() {
    const key = 'show_firebase_in_console_log';
    try {
      if (localStorage.hasOwnProperty(key)) {
        const value = localStorage.getItem(key);
        return JSON.parse(value);
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

}

const maxRetryAttempts = 3;

const baseUrl = 'https://www.gstatic.com/firebasejs/7.21.1/';

function getAnalytics() {
  return (window as any).firebase.analytics();
}

export enum FirebaseEventsEnum {
  AddContact = 'add_contact',
  AddContactsRecipients = 'add_contacts_recipients',
  AddSalesTeamRecipients = 'add_sales_team_recipients',
  Archive = 'archive',
  BulkUpdate = 'bulk_update',
  ChangeDetailView = 'change_detail_view',
  ChangePipeline = 'change_pipeline',
  ChangeLeadProcess = 'change_lead_process',
  ChangeQuoteProcess = 'change_quote_process',
  ChangeType = 'change_type',
  ChangeView = 'change_view',
  Complete = 'complete',
  ContactSales = 'contact_sales',
  Copy = 'copy',
  CopyAddressFromAccount = 'copy_address_from_account',
  Create = 'create',
  CreateEmail = 'create_email',
  CreateFromEmail = 'create_from_email',
  CreateFromTemplate = 'create_from_template',
  Delete = 'delete',
  Enroll = 'enroll',
  Export = 'export',
  ExportVcf = 'export_vcf',
  FeatureAdoption = 'feature_adoption',
  FeaturePromotion = 'feature_promotion',
  FollowUp = 'follow_up',
  Forward = 'forward',
  Import = 'import',
  Login = 'login',
  MakeFavorite = 'make_favorite',
  Merge = 'merge',
  QuickMerge = 'quick_merge',
  Move = 'move',
  NewReport = 'new_report',
  Ownership = 'ownership',
  Print = 'print',
  Qualify = 'qualify',
  Reactivate = 'reactivate',
  Reopen = 'reopen',
  Reply = 'reply',
  SaveEmail = 'save_email',
  SaveEmailAsDocument = 'save_email_as_document',
  SaveEmailAttachments = 'save_email_attachments',
  ScheduleReport = 'schedule_report',
  Send = 'send',
  SendReport = 'send_report',
  SessionStart = 'session_start',
  SetReminder = 'set_reminder',
  Sharing = 'sharing',
  TrackEmail = 'track_email',
  TriggerProcess = 'trigger_process',
  Update = 'update',
  Unsubscribe = 'unsubscribe',
  ViewChanges = 'view_changes',
  Win = 'win',
  ApprovalApprove = 'approval_approve',
  ApprovalReject = 'approval_reject',
  ApprovalCancel = 'approval_cancel',
  ApprovalDelegate = 'approval_delegate',
  AIGenerateEmail = 'ai_generate_email',
  AIGenerateEmailAppend = 'ai_generate_email_append',
  AIGenerateEmailReplace = 'ai_generate_email_replace',
  AIModifyTextAppend = 'ai_modify_text_append',
  AIModifyTextReplace = 'ai_modify_text_replace',
  AIModifyTextEnhanceAndComplete = 'ai_modify_text_enhance_and_complete',
  AIModifyTextSummarizeIntoBulletPoints = 'ai_modify_text_summarize_into_bullet_points',
  AIModifyTextSummarizeIntoParagraph = 'ai_modify_text_summarize_into_paragraph',
  AIModifyTextRephrase = 'ai_modify_text_rephrase',
  AIModifyTextMakeShorter = 'ai_modify_text_make_shorter',
  AIModifyTextMakeLonger = 'ai_modify_text_make_longer',
  AIModifyTextTextCorrection = 'ai_modify_text_text_correction',
  AIModifyTextChangeToneCold = 'ai_modify_text_change_tone_cold',
  AIModifyTextChangeToneProfessional = 'ai_modify_text_change_tone_professional',
  AIModifyTextChangeToneCasual = 'ai_modify_text_change_tone_casual',
  AIModifyTextChangeToneNeutral = 'ai_modify_text_change_tone_neutral',
  AIModifyTextChangeToneConfident = 'ai_modify_text_change_tone_confident',
  AIModifyTextChangeToneFriendly = 'ai_modify_text_change_tone_friendly',
  AITextAnalysis = 'ai_text_analysis',
  AISummary = 'ai_summary',
  AIActionPoints = 'ai_action_points',
  AIKeyPoints = 'ai_key_points',
  AISuggestReply = 'ai_suggest_reply',
  AIAskDocument = 'ai_ask_document',
  AIDocumentSummary = 'ai_document_summary',
  AITextToSpeechStartSynthesize = 'ai_text_to_speech_start_synthesize',
  OnlineFormSummarizationOpen = 'online_form_summarization_open',
  OnlineFormResponseOpen = 'online_form_response_open',
  SpeechToTextTranscribeStart = 'speech_to_text_transcribe_start',
  CustomizeLayout = 'customize_layout',
  ScheduleEmail = 'schedule_email'
}

export interface FirebaseLogData {
  current_screen: string;
  current_screen_settings: string;
  previous_screen?: string;
  previous_screen_settings?: string;
  entity?: string;
  entity_source?: string;
  entities?: string;
  settings?: string;
  feature?: string;
}

export interface FirebaseConfig {
  enabled: boolean;
  configData: FirebaseConfigData;
}

export interface FirebaseConfigData {
  apiKey: string;
  authDomain: string;
  databaseURL: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
}
